import { gql, useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { allStoresTableConfigs, useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IPurchaseSummaryType {
  getCustomizedReport: {
    nodes: {
      reportResult: {
        tableData: {
          itemDetails: Record<
            string,
            {
              item: string
              actualUsage: number
              actualUsagePercent: number
              end: number
              percent: {
                value: number
              }
            }
          >
          locationInfo: {
            id: number
          }
          netSales?: number
        }[]
      }
    }[]
  }
}

const query = gql`
  query CorporatePurchaseSummary($iInputParams: JSON!) {
    getCustomizedReport(
      iCustomizedReportName: "CORPORATE_PURCHASE_TABLE_V3"
      iInputParams: $iInputParams
    ) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }
  }
`

export const bbbCorporatePurchaseConfigs = {
  ...allStoresTableConfigs,
  netSales: 'price',
  actualUsage: 'price',
  actualUsagePercent: 'percent',
  endInventory: 'price',
  percentOfNetSales: 'percent',
  percentOfNetSalesHeatMap: 'percent',
} as const

const useBbbCorporatePurchase = () => {
  const dateFilter = useDateFilter()
  const { variables } = useVariables()
  const locationGroupIds = variables.allStores?.locationGroupIds
  const startDate = dateFilter.startDate
  const endDate = dateFilter.endDate
  const { data, loading } = useQuery<IPurchaseSummaryType>(query, {
    variables: {
      iInputParams: {
        locationGroupIds,
        startDate,
        endDate,
      },
    },
    skip: !startDate || !endDate || !locationGroupIds,
  })
  const bbbPurchaseCategories =
    variables.bbbPurchaseCategories?.value[0]?.slice(-1)?.[0]
  const bbbPurchaseCategoriesIsLoading =
    variables.bbbPurchaseCategories?.loading

  return {
    data: useMemo((): IApiDataType => {
      const tableData =
        data?.getCustomizedReport.nodes[0]?.reportResult.tableData

      if (data && !tableData) return []

      if (!tableData || !bbbPurchaseCategories) return null

      return tableData.map((t) => {
        const value = t.itemDetails[bbbPurchaseCategories]

        return {
          ...value,
          ...variables.allStores?.locations[t.locationInfo.id]?.tableRow,
          id: t.locationInfo.id.toString(),
          parentId: 'root',
          netSales: t.netSales,
          endInventory: value?.end,
          percentOfNetSales: value?.percent.value,
          percentOfNetSalesHeatmap: (() => {
            if (_.isNil(value?.percent.value)) return null

            if (value.percent.value >= 30) return 0

            if (value.percent.value < 30 && value.percent.value >= 20) return 1

            return 2
          })(),
        }
      })
    }, [data, bbbPurchaseCategories]),
    loading: loading || bbbPurchaseCategoriesIsLoading,
  }
}

export default useBbbCorporatePurchase
